/**
 * Tank Royale — world state + step.
 *
 * Top-down 2D arena. Up to 4 tanks. Movement: joystick heading + thrust.
 * Combat: tap A to fire a bullet from the tank's nose; B to drop a
 * shield that absorbs one hit for SHIELD_DURATION_S then enters a
 * cooldown. Last tank standing wins the round, then a short delay
 * before everyone respawns.
 *
 * No physics engine, just axis-aligned bounding-box collision against
 * a small list of walls and a per-tank radius test for hits.
 */

export const ARENA_W = 1600;
export const ARENA_H = 900;
export const TANK_RADIUS = 22;
export const TANK_MAX_SPEED = 220;       // px/sec
export const TANK_BACKWARD_SCALE = 0.5;  // we never reverse, just for completeness
export const BULLET_SPEED = 700;         // px/sec
export const BULLET_RADIUS = 4;
export const BULLET_TTL_S = 1.4;
export const TANK_MAX_HP = 3;
/** Humans get a one-HP advantage to balance against fighting 2-3 bots. */
export const HUMAN_MAX_HP = 4;
export const TANK_RELOAD_S = 0.42;
/** Bots reload slower than humans so a single human isn't outgunned by
 *  three bots firing at the same rate. */
export const AI_RELOAD_MULT = 1.55;
export const SHIELD_DURATION_S = 1.5;
export const SHIELD_COOLDOWN_S = 6;
export const ROUND_END_DELAY_S = 3.2;
export const ROUND_MIN_S = 0.8;          // grace before round-end can trigger
export const COUNTDOWN_S = 3.2;          // "3 · 2 · 1 · GO" hold time

export const PLAYER_COLOURS = ['#58ff58', '#ff8a3a', '#b48cff', '#ffd84a'];
export const MAX_PLAYERS = 4;

const SPAWN_POINTS: Array<{ x: number; y: number; heading: number }> = [
  { x: 160, y: 160,           heading: Math.PI / 4 },
  { x: ARENA_W - 160, y: 160, heading: 3 * Math.PI / 4 },
  { x: 160, y: ARENA_H - 160, heading: -Math.PI / 4 },
  { x: ARENA_W - 160, y: ARENA_H - 160, heading: -3 * Math.PI / 4 },
];

/** Cover crates — axis-aligned rectangles. Symmetric layout so no
 *  spawn corner is unfairly walled-in or exposed. */
export const WALLS: ReadonlyArray<{ x: number; y: number; w: number; h: number }> = [
  // Central cross
  { x: ARENA_W / 2 - 90, y: ARENA_H / 2 - 16, w: 180, h: 32 },
  { x: ARENA_W / 2 - 16, y: ARENA_H / 2 - 90, w: 32, h: 180 },
  // Four diagonal crates
  { x: 320,           y: 280,            w: 90, h: 90 },
  { x: ARENA_W - 410, y: 280,            w: 90, h: 90 },
  { x: 320,           y: ARENA_H - 370,  w: 90, h: 90 },
  { x: ARENA_W - 410, y: ARENA_H - 370,  w: 90, h: 90 },
];

export interface Tank {
  p: number;
  colour: string;
  alive: boolean;
  hp: number;
  x: number;
  y: number;
  heading: number;        // radians, 0 = right
  thrust: boolean;
  reloadUntil: number;    // game-time when reload completes
  shieldUntil: number;    // game-time shield drops
  shieldCdUntil: number;  // game-time cooldown ends
  score: number;
  /** Game-time the next tread mark is due — internal renderer hint. */
  treadDueAt: number;
  /** True for host-side bots filling otherwise-empty slots. */
  ai: boolean;
  /** Game-time the bot may next change its strategic intent. */
  aiNextDecisionAt: number;
  /** Current micro-intent: chase, strafe, retreat. */
  aiIntent: 'chase' | 'strafe' | 'retreat';
  /** Per-bot personality for target selection — fixed per spawn so
   *  each bot has a recognisable rhythm. 'hunter' goes after humans
   *  first, 'scrapper' picks the weakest enemy, 'opportunist' picks
   *  the nearest. */
  aiPersonality: 'hunter' | 'scrapper' | 'opportunist';
}

export interface Bullet {
  ownerP: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  ttl: number;
}

export type RoundState =
  | { kind: 'lobby' }                          // waiting for first players
  | { kind: 'countdown'; startedAt: number }   // 3-2-1-GO hold before play
  | { kind: 'playing'; startedAt: number }     // round in progress
  | { kind: 'over'; winnerP: number | null; endedAt: number };

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;          // seconds remaining
  maxLife: number;       // initial seconds (for fade)
  size: number;
  colour: string;
}

/** Screen shake — peak displacement in arena units, decays linearly. */
export interface Shake {
  intensity: number;
  duration: number;       // seconds
  startedAt: number;      // game-time
}

/** Tread mark left on the arena floor as a tank moves. Capped per
 *  world to avoid unbounded growth; renderer fades them out by age. */
export interface TreadMark {
  x: number;
  y: number;
  age: number;             // seconds since the mark was laid
  colour: string;
  heading: number;         // tank heading at the time, for rotated draws
}
const MAX_TREADS = 240;
const TREAD_LIFETIME = 8.0;        // seconds before the renderer fades it out
const TREAD_INTERVAL = 0.045;      // seconds between marks for a moving tank

/** Radial light flash — overlay drawn additive over the arena, used for
 *  kill bursts and other dramatic beats. */
export interface Flash {
  x: number;
  y: number;
  startedAt: number;       // game-time
  duration: number;        // seconds
  radius: number;          // peak px
  colour: string;
}

export interface World {
  tanks: Map<number, Tank>;
  bullets: Bullet[];
  particles: Particle[];
  shake: Shake | null;
  treads: TreadMark[];
  flashes: Flash[];
  round: RoundState;
  time: number;     // accumulated game time in seconds
  /** Game-time the world last entered lobby state — used by the attract-
   *  mode kick-in timer. */
  lobbyEnteredAt: number;
  /** True while a bot-only demo round is playing because the room is
   *  empty. Cleared as soon as any human pairs. */
  attractMode: boolean;
}

/** Seconds to sit in lobby with zero humans before kicking into the
 *  bot-only attract demo. */
export const ATTRACT_DELAY = 15.0;

export function newWorld(): World {
  return {
    tanks: new Map(),
    bullets: [],
    particles: [],
    shake: null,
    treads: [],
    flashes: [],
    round: { kind: 'lobby' },
    time: 0,
    lobbyEnteredAt: 0,
    attractMode: false,
  };
}

function spawnParticles(world: World, opts: {
  x: number; y: number; count: number;
  speed: number; spread?: number;
  life: number; size: number; colour: string;
  baseHeading?: number;
}): void {
  const baseH = opts.baseHeading ?? null;
  const spread = opts.spread ?? Math.PI * 2;
  for (let i = 0; i < opts.count; i++) {
    const heading = baseH !== null
      ? baseH + (Math.random() - 0.5) * spread
      : Math.random() * Math.PI * 2;
    const speed = opts.speed * (0.5 + Math.random() * 0.5);
    const life = opts.life * (0.7 + Math.random() * 0.6);
    world.particles.push({
      x: opts.x, y: opts.y,
      vx: Math.cos(heading) * speed,
      vy: Math.sin(heading) * speed,
      life, maxLife: life,
      size: opts.size * (0.7 + Math.random() * 0.6),
      colour: opts.colour,
    });
  }
}

export function triggerShake(world: World, intensity: number, duration: number): void {
  // Replace if the new shake is stronger or the old has decayed.
  if (!world.shake) {
    world.shake = { intensity, duration, startedAt: world.time };
    return;
  }
  const elapsed = world.time - world.shake.startedAt;
  const remainingIntensity = Math.max(0, world.shake.intensity * (1 - elapsed / world.shake.duration));
  if (intensity > remainingIntensity) {
    world.shake = { intensity, duration, startedAt: world.time };
  }
}

export function spawnTank(world: World, p: number, ai = false): Tank {
  // Pick the spawn point matching player slot, or wrap if slot is out
  // of range (shouldn't happen — broker caps at MAX_PHONES_PER_SESSION).
  const spot = SPAWN_POINTS[p % SPAWN_POINTS.length]!;
  const tank: Tank = {
    p,
    colour: PLAYER_COLOURS[p % PLAYER_COLOURS.length]!,
    alive: true,
    hp: ai ? TANK_MAX_HP : HUMAN_MAX_HP,
    x: spot.x,
    y: spot.y,
    heading: spot.heading,
    thrust: false,
    reloadUntil: 0,
    shieldUntil: 0,
    shieldCdUntil: 0,
    score: 0,
    treadDueAt: 0,
    ai,
    aiNextDecisionAt: 0,
    aiIntent: 'chase',
    aiPersonality: pickPersonality(),
  };
  world.tanks.set(p, tank);
  return tank;
}

function pickPersonality(): Tank['aiPersonality'] {
  const r = Math.random();
  // Fewer hunters so a lone human doesn't get triple-teamed. Most
  // bots now just chase the nearest enemy or pick on the weakest,
  // which means they often fight each other.
  if (r < 0.20) return 'hunter';
  if (r < 0.60) return 'scrapper';
  return 'opportunist';
}

/** Fill empty player slots with AI tanks so a single human always
 *  has 3 opponents to play against — and a passer-by always sees a
 *  full arena worth of action. */
export function autoFillAI(world: World): void {
  for (let p = 0; p < MAX_PLAYERS; p++) {
    if (!world.tanks.has(p)) spawnTank(world, p, true);
  }
}

/** Called by the input handler. value is the wire value (string). */
export function applyInput(world: World, p: number, slot: string, value: string): void {
  const tank = world.tanks.get(p);
  if (!tank) return;
  // Any input from a human while the world is in lobby state counts
  // as "start signal" — fills bots in, kicks the round off.
  if (world.round.kind === 'lobby' && !tank.ai) {
    if (!['A', 'B', 'joyL-tap', 'joyL-thrust'].includes(slot) || value !== '1') return;
    autoFillAI(world);
    world.round = { kind: 'countdown', startedAt: world.time };
    return;
  }
  // Block firing during countdown but let movement happen so players
  // can position themselves before the start.
  if (world.round.kind === 'countdown' && (slot === 'A' || slot === 'joyL-tap')) {
    return;
  }
  if (!tank.alive) return;
  const on = value === '1';
  switch (slot) {
    case 'joyL': {
      const angle = parseInt(value, 10) / 1000;
      if (Number.isFinite(angle)) tank.heading = angle;
      return;
    }
    case 'joyL-thrust':
      tank.thrust = on;
      return;
    case 'joyL-end':
      tank.thrust = false;
      return;
    case 'joyL-tap':
      // Treat tap-fire as a fire press.
      if (on) tryFire(world, tank);
      return;
    case 'A':
      if (on) tryFire(world, tank);
      return;
    case 'B':
      if (on) tryShield(world, tank);
      return;
  }
}

function tryFire(world: World, tank: Tank): void {
  if (world.time < tank.reloadUntil) return;
  tank.reloadUntil = world.time + TANK_RELOAD_S * (tank.ai ? AI_RELOAD_MULT : 1);
  // Spawn bullet just past the tank's nose so we don't immediately
  // collide with ourselves.
  const offset = TANK_RADIUS + BULLET_RADIUS + 2;
  const bullet: Bullet = {
    ownerP: tank.p,
    x: tank.x + Math.cos(tank.heading) * offset,
    y: tank.y + Math.sin(tank.heading) * offset,
    vx: Math.cos(tank.heading) * BULLET_SPEED,
    vy: Math.sin(tank.heading) * BULLET_SPEED,
    ttl: BULLET_TTL_S,
  };
  world.bullets.push(bullet);
}

function tryShield(world: World, tank: Tank): void {
  if (world.time < tank.shieldCdUntil) return;
  tank.shieldUntil = world.time + SHIELD_DURATION_S;
  tank.shieldCdUntil = world.time + SHIELD_COOLDOWN_S;
  // Cyan ring expansion — particles fly outward from the tank.
  spawnParticles(world, {
    x: tank.x, y: tank.y, count: 16,
    speed: 220, life: 0.32, size: 3,
    colour: 'rgba(120,220,255,0.85)',
  });
}

/** Advance the world by dt seconds. */
export function step(world: World, dt: number): void {
  world.time += dt;

  // Attract-mode kick-out — if a human has paired while we're running
  // a bot-only demo, drop all bots and return to lobby so the human
  // can fire their own round.
  if (world.attractMode && humanCount(world) > 0) {
    for (const tank of [...world.tanks.values()]) {
      if (tank.ai) world.tanks.delete(tank.p);
    }
    world.bullets.length = 0;
    world.particles.length = 0;
    world.treads.length = 0;
    world.flashes.length = 0;
    world.shake = null;
    world.round = { kind: 'lobby' };
    world.attractMode = false;
    world.lobbyEnteredAt = world.time;
  }

  // Attract-mode kick-in — empty lobby for ATTRACT_DELAY seconds:
  // autoFillAI and start a countdown so passers-by see motion.
  if (
    world.round.kind === 'lobby'
    && world.tanks.size === 0
    && !world.attractMode
    && world.time - world.lobbyEnteredAt >= ATTRACT_DELAY
  ) {
    autoFillAI(world);
    world.attractMode = true;
    world.round = { kind: 'countdown', startedAt: world.time };
  }

  // AI tick first so bot inputs land before movement integrates.
  // Bots are quiet during countdown — gives the visual a moment to
  // breathe and players a chance to read the arena.
  for (const tank of world.tanks.values()) {
    if (tank.ai && tank.alive && world.round.kind === 'playing') {
      aiTick(world, tank, dt);
    } else if (tank.ai && world.round.kind === 'countdown') {
      tank.thrust = false;
    }
  }

  // Tanks
  for (const tank of world.tanks.values()) {
    if (!tank.alive) continue;
    if (tank.thrust) {
      const newX = tank.x + Math.cos(tank.heading) * TANK_MAX_SPEED * dt;
      const newY = tank.y + Math.sin(tank.heading) * TANK_MAX_SPEED * dt;
      // Try X then Y independently so we slide along walls instead of
      // dead-stopping when hitting a corner.
      if (canPlaceTank(newX, tank.y, tank.p, world)) tank.x = newX;
      if (canPlaceTank(tank.x, newY, tank.p, world)) tank.y = newY;
      // Lay tread marks at a fixed cadence while moving so the floor
      // accumulates a sense of momentum.
      if (world.time >= tank.treadDueAt) {
        tank.treadDueAt = world.time + TREAD_INTERVAL;
        world.treads.push({ x: tank.x, y: tank.y, age: 0, colour: tank.colour, heading: tank.heading });
        if (world.treads.length > MAX_TREADS) world.treads.shift();
      }
    }
  }

  // Tick treads — age and drop expired marks. Linear-time over the
  // capped array, fine even at MAX_TREADS each frame.
  for (let i = world.treads.length - 1; i >= 0; i--) {
    world.treads[i]!.age += dt;
    if (world.treads[i]!.age >= TREAD_LIFETIME) world.treads.splice(i, 1);
  }

  // Tick flashes — drop expired.
  for (let i = world.flashes.length - 1; i >= 0; i--) {
    if (world.time - world.flashes[i]!.startedAt > world.flashes[i]!.duration) {
      world.flashes.splice(i, 1);
    }
  }

  // Bullets
  for (let i = world.bullets.length - 1; i >= 0; i--) {
    const b = world.bullets[i]!;
    b.x += b.vx * dt;
    b.y += b.vy * dt;
    b.ttl -= dt;
    let killBullet = b.ttl <= 0
      || b.x < BULLET_RADIUS || b.x > ARENA_W - BULLET_RADIUS
      || b.y < BULLET_RADIUS || b.y > ARENA_H - BULLET_RADIUS;
    if (!killBullet) {
      for (const wall of WALLS) {
        if (b.x > wall.x && b.x < wall.x + wall.w && b.y > wall.y && b.y < wall.y + wall.h) {
          killBullet = true;
          // Small wall spark — perpendicular-ish to bullet travel.
          spawnParticles(world, {
            x: b.x, y: b.y, count: 5,
            speed: 180, spread: Math.PI * 0.9,
            life: 0.18, size: 2, colour: 'rgba(207,216,255,0.85)',
            baseHeading: Math.atan2(-b.vy, -b.vx),
          });
          break;
        }
      }
    }
    if (!killBullet) {
      for (const tank of world.tanks.values()) {
        if (!tank.alive) continue;
        if (tank.p === b.ownerP) continue;
        const dx = tank.x - b.x;
        const dy = tank.y - b.y;
        if (dx * dx + dy * dy < (TANK_RADIUS + BULLET_RADIUS) * (TANK_RADIUS + BULLET_RADIUS)) {
          killBullet = true;
          // Bullet-direction so sparks fly away from the impact.
          const bulletHeading = Math.atan2(b.vy, b.vx);
          if (world.time < tank.shieldUntil) {
            // Shield absorbs the hit and ends — cyan ring scatter.
            tank.shieldUntil = world.time;
            spawnParticles(world, {
              x: b.x, y: b.y, count: 12,
              speed: 260, spread: Math.PI * 1.4,
              life: 0.35, size: 3, colour: 'rgba(120,220,255,0.95)',
              baseHeading: bulletHeading,
            });
          } else {
            tank.hp -= 1;
            // Yellow-orange hit sparks.
            spawnParticles(world, {
              x: b.x, y: b.y, count: 10,
              speed: 280, spread: Math.PI * 1.2,
              life: 0.30, size: 3, colour: '#ffd84a',
              baseHeading: bulletHeading,
            });
            if (tank.hp <= 0) {
              tank.alive = false;
              tank.thrust = false;
              // Explosion — particles in the tank's colour for tribal
              // attribution + a generic spray of yellow + soot.
              spawnParticles(world, {
                x: tank.x, y: tank.y, count: 28,
                speed: 360, life: 0.65, size: 4, colour: tank.colour,
              });
              spawnParticles(world, {
                x: tank.x, y: tank.y, count: 18,
                speed: 260, life: 0.75, size: 3, colour: '#ff8a3a',
              });
              spawnParticles(world, {
                x: tank.x, y: tank.y, count: 14,
                speed: 180, life: 0.95, size: 5, colour: 'rgba(40,40,55,0.85)',
              });
              // White light flash on death — bright radial pulse so the
              // moment of detonation reads as cinematic.
              world.flashes.push({
                x: tank.x, y: tank.y,
                startedAt: world.time, duration: 0.55,
                radius: 140, colour: '#ffffff',
              });
              triggerShake(world, 14, 0.42);
              // Killer gets a score bump.
              const killer = world.tanks.get(b.ownerP);
              if (killer) killer.score += 1;
            }
          }
          break;
        }
      }
    }
    if (killBullet) world.bullets.splice(i, 1);
  }

  // Particles — integrate position, decay life, drop the dead.
  for (let i = world.particles.length - 1; i >= 0; i--) {
    const p = world.particles[i]!;
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    // Mild drag so explosion sparks don't fly forever.
    p.vx *= 0.985;
    p.vy *= 0.985;
    p.life -= dt;
    if (p.life <= 0) world.particles.splice(i, 1);
  }
  // Screen shake — auto-clear once duration elapses (renderer reads).
  if (world.shake && world.time - world.shake.startedAt >= world.shake.duration) {
    world.shake = null;
  }

  // Round state machine
  if (world.round.kind === 'countdown') {
    if (world.time - world.round.startedAt >= COUNTDOWN_S) {
      world.round = { kind: 'playing', startedAt: world.time };
    }
  } else if (world.round.kind === 'playing') {
    if (world.time - world.round.startedAt > ROUND_MIN_S) {
      const alive = [...world.tanks.values()].filter((t) => t.alive);
      if (alive.length <= 1 && world.tanks.size >= 1) {
        const winnerP = alive.length === 1 ? alive[0]!.p : null;
        world.round = { kind: 'over', winnerP, endedAt: world.time };
      }
    }
  } else if (world.round.kind === 'over') {
    if (world.time - world.round.endedAt >= ROUND_END_DELAY_S) {
      const hasHumans = [...world.tanks.values()].some((t) => !t.ai);
      if (hasHumans) {
        // Respawn everyone for the next round. Humans stay paired,
        // bots are reset along with them — the action continues.
        for (const tank of world.tanks.values()) {
          const spot = SPAWN_POINTS[tank.p % SPAWN_POINTS.length]!;
          tank.x = spot.x; tank.y = spot.y;
          tank.heading = spot.heading;
          tank.thrust = false;
          tank.alive = true;
          tank.hp = tank.ai ? TANK_MAX_HP : HUMAN_MAX_HP;
          tank.reloadUntil = 0;
          tank.shieldUntil = 0;
          tank.shieldCdUntil = 0;
        }
        world.bullets.length = 0;
        world.particles.length = 0;
        world.treads.length = 0;
        world.flashes.length = 0;
        world.shake = null;
        world.round = { kind: 'countdown', startedAt: world.time };
      } else if (world.attractMode) {
        // Bot-only attract round just ended — loop straight back into
        // another countdown so the demo never stops showing motion.
        for (const tank of world.tanks.values()) {
          const spot = SPAWN_POINTS[tank.p % SPAWN_POINTS.length]!;
          tank.x = spot.x; tank.y = spot.y;
          tank.heading = spot.heading;
          tank.thrust = false;
          tank.alive = true;
          tank.hp = tank.ai ? TANK_MAX_HP : HUMAN_MAX_HP;
          tank.reloadUntil = 0;
          tank.shieldUntil = 0;
          tank.shieldCdUntil = 0;
        }
        world.bullets.length = 0;
        world.particles.length = 0;
        world.treads.length = 0;
        world.flashes.length = 0;
        world.shake = null;
        world.round = { kind: 'countdown', startedAt: world.time };
      } else {
        // No humans left — drop the bots and return to lobby. Next
        // human to scan + press will kick the action off again.
        for (const tank of [...world.tanks.values()]) {
          if (tank.ai) world.tanks.delete(tank.p);
        }
        world.bullets.length = 0;
        world.particles.length = 0;
        world.treads.length = 0;
        world.flashes.length = 0;
        world.shake = null;
        world.round = { kind: 'lobby' };
        world.lobbyEnteredAt = world.time;
      }
    }
  }
}

/** Drop a tank when the player disconnects. If it was the last living
 *  one we still let the round-over flow fire, so the scoreboard
 *  shows. If the round is in lobby and we just lost everyone, stay in
 *  lobby. */
export function removeTank(world: World, p: number): void {
  const tank = world.tanks.get(p);
  if (!tank) return;
  world.tanks.delete(p);
  // Bullets in flight keep going — their owner is gone but they still
  // count. Cheap and feels fair.
  if (world.tanks.size === 0 && world.round.kind !== 'lobby') {
    world.round = { kind: 'lobby' };
    world.lobbyEnteredAt = world.time;
  }
}

/** Count human-controlled tanks currently in the world. */
export function humanCount(world: World): number {
  let n = 0;
  for (const t of world.tanks.values()) if (!t.ai) n += 1;
  return n;
}

/** Per-bot brain v2. Personality-based target selection (hunter /
 *  scrapper / opportunist), bullet-intercept leading for moving
 *  targets, proactive shield against incoming bullets, periodic
 *  intent rotation (chase / strafe / retreat), wall lookahead. */
function aiTick(world: World, tank: Tank, _dt: number): void {
  const target = pickTarget(world, tank);
  if (!target) { tank.thrust = false; return; }

  // Pick a fresh intent every 1.2-2.0s — keeps the bot from sniping
  // statically and gives a human something to read.
  if (world.time >= tank.aiNextDecisionAt) {
    const intents: Tank['aiIntent'][] = ['chase', 'strafe', 'retreat'];
    const dist = Math.hypot(target.x - tank.x, target.y - tank.y);
    // Bias toward chase when far, strafe when mid, retreat when low HP.
    let pool: Tank['aiIntent'][];
    if (tank.hp === 1) pool = ['retreat', 'strafe', 'strafe'];
    else if (dist > 380) pool = ['chase', 'chase', 'strafe'];
    else if (dist < 180) pool = ['retreat', 'strafe', 'strafe'];
    else pool = ['strafe', 'chase', 'strafe'];
    tank.aiIntent = pool[Math.floor(Math.random() * pool.length)] ?? intents[0]!;
    tank.aiNextDecisionAt = world.time + 1.2 + Math.random() * 0.8;
  }

  // Lead the target by predicting where it will be when the bullet
  // arrives. Iterate twice for a closer fix — first pass approximates
  // travel time from current distance, second pass refines using the
  // distance to the predicted lead point.
  let leadX = target.x, leadY = target.y;
  {
    const tvx = target.thrust ? Math.cos(target.heading) * TANK_MAX_SPEED : 0;
    const tvy = target.thrust ? Math.sin(target.heading) * TANK_MAX_SPEED : 0;
    for (let i = 0; i < 2; i++) {
      const d = Math.hypot(leadX - tank.x, leadY - tank.y);
      const tof = d / BULLET_SPEED;
      leadX = target.x + tvx * tof;
      leadY = target.y + tvy * tof;
    }
  }
  const desired = Math.atan2(leadY - tank.y, leadX - tank.x);

  // Per-bot aim noise. Sized so bots feel competent but miss more
  // often than they hit at full distance — gives the human room to
  // dodge instead of being instantly head-shot every time they're
  // visible.
  const aimNoise = 0.085 * Math.sin(world.time * 1.7 + tank.p * 5);
  const turn = clampAngle(desired - tank.heading + aimNoise);
  const maxTurnPerTick = 4.5 * 0.022;
  tank.heading += Math.sign(turn) * Math.min(Math.abs(turn), maxTurnPerTick);

  // Move based on intent.
  if (tank.aiIntent === 'chase') {
    tank.heading = approachAngle(tank.heading, desired, 0.15);
    tank.thrust = true;
  } else if (tank.aiIntent === 'strafe') {
    const lateral = desired + (Math.sin(world.time * 2 + tank.p) > 0 ? Math.PI / 2 : -Math.PI / 2);
    tank.heading = approachAngle(tank.heading, lateral, 0.10);
    tank.thrust = true;
  } else {
    // Retreat — head perpendicular AND away. Splits the difference so
    // the bot moves to cover instead of running in a straight line.
    const away = desired + Math.PI;
    const lateral = desired + (Math.sin(world.time * 1.4 + tank.p) > 0 ? Math.PI / 2 : -Math.PI / 2);
    tank.heading = approachAngle(tank.heading, (away + lateral) / 2, 0.12);
    tank.thrust = true;
  }

  // Fire when reloaded + aligned. Tighter slack now that we lead;
  // still loose enough that mid-air target swerves aren't perfectly
  // tracked.
  const dist = Math.hypot(leadX - tank.x, leadY - tank.y);
  const aimSlack = 0.06 + dist / 3000;
  if (world.time >= tank.reloadUntil
   && Math.abs(clampAngle(desired - tank.heading)) < aimSlack
   && lineOfFireClear(tank, { x: leadX, y: leadY })) {
    tryFire(world, tank);
  }

  // Shield: reactive — bots only burn a shield in real danger now,
  // not on every distant snap-shot. Triggers when:
  //   - HP is at 1 AND a bullet is heading at us within 0.6s, OR
  //   - 2+ bullets converging on us within 0.45s (a clear crossfire)
  // This stops bots from popping the bubble at every incoming round,
  // which made it nearly impossible for a human to land a hit.
  if (world.time >= tank.shieldCdUntil) {
    if (tank.hp === 1 && incomingBullet(world, tank, 0.6)) {
      tryShield(world, tank);
    } else if (incomingBullets(world, tank, 0.45) >= 2) {
      tryShield(world, tank);
    }
  }

  // Wall avoidance — if a 60px lookahead would clip, rotate 45°.
  const lookX = tank.x + Math.cos(tank.heading) * (TANK_RADIUS + 60);
  const lookY = tank.y + Math.sin(tank.heading) * (TANK_RADIUS + 60);
  if (!canPlaceTank(lookX, lookY, tank.p, world)) {
    tank.heading += Math.PI / 4;
  }
}

function pickTarget(world: World, self: Tank): Tank | null {
  const enemies: Tank[] = [];
  for (const other of world.tanks.values()) {
    if (other.p === self.p || !other.alive) continue;
    enemies.push(other);
  }
  if (enemies.length === 0) return null;
  if (self.aiPersonality === 'hunter') {
    // Prefer the closest human; fall back to nearest enemy.
    const humans = enemies.filter((e) => !e.ai);
    if (humans.length > 0) return nearest(self, humans);
  }
  if (self.aiPersonality === 'scrapper') {
    // Lowest-HP enemy — break ties by distance for snappier behaviour.
    let best = enemies[0]!;
    for (const e of enemies) {
      if (e.hp < best.hp) best = e;
      else if (e.hp === best.hp) {
        const dE = (e.x - self.x) ** 2 + (e.y - self.y) ** 2;
        const dB = (best.x - self.x) ** 2 + (best.y - self.y) ** 2;
        if (dE < dB) best = e;
      }
    }
    return best;
  }
  return nearest(self, enemies);
}

function nearest(self: Tank, candidates: Tank[]): Tank {
  let best = candidates[0]!;
  let bestD = (best.x - self.x) ** 2 + (best.y - self.y) ** 2;
  for (let i = 1; i < candidates.length; i++) {
    const c = candidates[i]!;
    const d = (c.x - self.x) ** 2 + (c.y - self.y) ** 2;
    if (d < bestD) { bestD = d; best = c; }
  }
  return best;
}

/** True if any bullet not owned by self will pass within TANK_RADIUS
 *  of self over the next `lookaheadS` seconds. Cheap segment-point
 *  distance check; runs each tick for each bot but the bullet count
 *  stays small. */
function incomingBullet(world: World, self: Tank, lookaheadS: number): boolean {
  return incomingBullets(world, self, lookaheadS) > 0;
}

/** Count distinct enemy bullets whose path over the next lookaheadS
 *  seconds would cross the tank's body. Used by the bot's shield AI
 *  to distinguish 'one stray shot' from 'a real crossfire'. */
function incomingBullets(world: World, self: Tank, lookaheadS: number): number {
  const r = TANK_RADIUS * 1.35;
  let count = 0;
  for (const b of world.bullets) {
    if (b.ownerP === self.p) continue;
    const ex = b.x + b.vx * lookaheadS;
    const ey = b.y + b.vy * lookaheadS;
    const dx = ex - b.x, dy = ey - b.y;
    const len2 = dx * dx + dy * dy;
    if (len2 < 1) continue;
    let t = ((self.x - b.x) * dx + (self.y - b.y) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const cx = b.x + dx * t;
    const cy = b.y + dy * t;
    const distSq = (self.x - cx) ** 2 + (self.y - cy) ** 2;
    if (distSq < r * r) count += 1;
  }
  return count;
}

function lineOfFireClear(self: Tank, target: { x: number; y: number }): boolean {
  // Sample 8 points between self and target; if any sits inside a
  // wall, the shot is blocked.
  const steps = 8;
  const dx = (target.x - self.x) / steps;
  const dy = (target.y - self.y) / steps;
  for (let i = 1; i < steps; i++) {
    const x = self.x + dx * i;
    const y = self.y + dy * i;
    for (const wall of WALLS) {
      if (x > wall.x && x < wall.x + wall.w && y > wall.y && y < wall.y + wall.h) return false;
    }
  }
  return true;
}

function underThreat(world: World, self: Tank): boolean {
  // Any bullet headed our way OR an enemy aiming at us, within 320px.
  for (const b of world.bullets) {
    if (b.ownerP === self.p) continue;
    const dx = self.x - b.x, dy = self.y - b.y;
    if (dx * dx + dy * dy < 320 * 320) return true;
  }
  for (const other of world.tanks.values()) {
    if (other.p === self.p || !other.alive) continue;
    const aimDelta = Math.atan2(self.y - other.y, self.x - other.x) - other.heading;
    if (Math.abs(clampAngle(aimDelta)) < 0.2) return true;
  }
  return false;
}

function clampAngle(a: number): number {
  // Normalise to (-PI, PI].
  while (a > Math.PI) a -= Math.PI * 2;
  while (a <= -Math.PI) a += Math.PI * 2;
  return a;
}

function approachAngle(from: number, to: number, k: number): number {
  return from + clampAngle(to - from) * k;
}

function canPlaceTank(x: number, y: number, p: number, world: World): boolean {
  // Arena bounds.
  if (x < TANK_RADIUS || x > ARENA_W - TANK_RADIUS) return false;
  if (y < TANK_RADIUS || y > ARENA_H - TANK_RADIUS) return false;
  // Walls.
  for (const wall of WALLS) {
    const cx = Math.max(wall.x, Math.min(x, wall.x + wall.w));
    const cy = Math.max(wall.y, Math.min(y, wall.y + wall.h));
    const dx = x - cx, dy = y - cy;
    if (dx * dx + dy * dy < TANK_RADIUS * TANK_RADIUS) return false;
  }
  // Other tanks — soft separation so two tanks can't share a cell.
  for (const other of world.tanks.values()) {
    if (other.p === p || !other.alive) continue;
    const dx = x - other.x, dy = y - other.y;
    if (dx * dx + dy * dy < (TANK_RADIUS * 2 - 6) * (TANK_RADIUS * 2 - 6)) return false;
  }
  return true;
}
